import { distinctBy } from "../../../../utilities/arrayHelper";
import {
  DimensionDescriptor,
  DimensionFieldType,
  ReportField,
  ReportType,
  RunReportConfigurationBase,
} from "../../../api/biClient.types";
import { chartRelatedReportTypes } from "../../../utils/chartRelatedReport.types";
import {
  getChartDimensionNames,
  getPivotDimensionNames,
  getTabularDimensionNames,
  getTileDimensionNames,
} from "../../../utils/getDimensionNames";
import {
  getChartMeasureNames,
  getPivotMeasureNames,
  getTabularMeasureNames,
  getTileMeasureNames,
} from "../../../utils/getMeasureNames";
import {
  ChartRunState,
  PivotRunState,
  ReportConfigurationForRunning,
  RunStateAction,
  RunStateBase,
  TabularRunState,
  TileRunState,
} from "./runState";

export const isTileRunState = (runState: ReportConfigurationForRunning): runState is TileRunState => {
  return runState.reportType === ReportType.Tile;
};

export const isTabularRunState = (runState: ReportConfigurationForRunning): runState is TabularRunState => {
  return runState.reportType === ReportType.Tabular;
};

export const isPivotRunState = (runState: ReportConfigurationForRunning): runState is PivotRunState => {
  return runState.reportType === ReportType.Pivot;
};

export const isChartRunState = (runState: ReportConfigurationForRunning): runState is ChartRunState => {
  return chartRelatedReportTypes.includes(runState.reportType);
};

export const initializeCommonRunState = (
  action: Extract<RunStateAction, { type: "Initialize" }>
): RunReportConfigurationBase & RunStateBase => {
  return {
    dataSetId: action.payload.response.dataSetId,
    reportType: action.payload.response.reportType,
    conditions: action.payload.response.conditions,
    showParameters: action.payload.response.showParameters,
    fieldIdsWithCustomConditionParameters: action.payload.response.fieldIdsWithCustomConditionParameters,
    books: action.payload.response.books,
    isMultiBook: action.payload.response.isMultiBook,
    customConditionsMeta: action.payload.response.customConditionsMeta,
    refreshToken: "",
    dimensionNames: getDimensionNames(action.payload.response),
    measureNames: getMeasureNames(action.payload.response),
    fieldsWithCustomConditionParameters: getFieldsWithCustomConditionParameters(action.payload.response),
    dimensions: getDimensionsFromReportConfiguration(action.payload.response),
  };
};

const getFieldsWithCustomConditionParameters = (config: ReportConfigurationForRunning): ReportField[] => {
  if (isTabularRunState(config) || isTileRunState(config)) {
    const { fieldIdsWithCustomConditionParameters } = config;
    return config.fields.filter((f) => fieldIdsWithCustomConditionParameters.includes(f.config.guid));
  }
  if (isPivotRunState(config) || isChartRunState(config)) {
    const { fieldIdsWithCustomConditionParameters } = config;
    return config.values.filter((f) => fieldIdsWithCustomConditionParameters.includes(f.config.guid));
  }
  throw new Error("Invalid report type");
};

const getDimensionsFromReportConfiguration = (config: ReportConfigurationForRunning): DimensionDescriptor[] => {
  return distinctBy(
    config.conditions
      .map((c) => c.meta)
      .concat(config.customConditionsMeta)
      .concat(getDimensionsFromFields(config)),
    (d) => d.name
  );
};

const getDimensionsFromFields = (config: ReportConfigurationForRunning) => {
  if (isTabularRunState(config) || isTileRunState(config)) {
    return config.fields.filter((m) => m.type === DimensionFieldType.DIMENSION).map((f) => f.meta);
  }
  if (isPivotRunState(config)) {
    return config.rows
      .concat(config.columns)
      .concat(config.values.filter((v) => v.type === DimensionFieldType.DIMENSION))
      .map((r) => r.meta);
  }
  if (isChartRunState(config)) {
    return config.arguments
      .concat(config.values.filter((v) => v.type === DimensionFieldType.DIMENSION))
      .map((r) => r.meta);
  }
  throw new Error("Invalid report type");
};

const getMeasureNames = (config: ReportConfigurationForRunning): string[] => {
  if (isTabularRunState(config)) {
    return getTabularMeasureNames(config.fields.map((f) => f.config));
  }
  if (isPivotRunState(config)) {
    return getPivotMeasureNames(config.values.map((v) => v.config));
  }
  if (isChartRunState(config)) {
    return getChartMeasureNames(config.values.map((v) => v.config));
  }
  if (isTileRunState(config)) {
    return getTileMeasureNames(config.fields.map((f) => f.config));
  }
  throw new Error("Invalid report type");
};

const getDimensionNames = (config: ReportConfigurationForRunning): string[] => {
  if (isTabularRunState(config)) {
    return getTabularDimensionNames(
      config.fields.map((f) => f.config),
      config.conditions.map((c) => c.config)
    );
  }

  if (isPivotRunState(config)) {
    return getPivotDimensionNames(
      config.conditions.map((c) => c.config),
      config.rows.map((r) => r.config),
      config.columns.map((c) => c.config)
    );
  }
  if (isChartRunState(config)) {
    return getChartDimensionNames(
      config.conditions.map((c) => c.config),
      config.arguments.map((a) => a.config)
    );
  }
  if (isTileRunState(config)) {
    return getTileDimensionNames(
      config.fields.map((f) => f.config),
      config.conditions.map((c) => c.config)
    );
  }
  throw new Error("Invalid report type");
};
